import assert from '@banking/ui/utils/assert';
import workflow from '@banking/ui/lib/workflow';
import { CardInvitation, RetrieveCardInvitationRequest, RevokeCardInvitationRequest, SendCardInvitationRequest } from '@square/dashboard-protos/models/squareup/banking/card_invitation';

// State
// =============================================================================

export let ResendToastStatus = /*#__PURE__*/function (ResendToastStatus) {
  ResendToastStatus["None"] = "NONE";
  ResendToastStatus["Success"] = "SUCCESS";
  ResendToastStatus["Failure"] = "FAILURE";
  return ResendToastStatus;
}({});
export let Error = /*#__PURE__*/function (Error) {
  Error["InvitationFetchError"] = "INVITATION_FETCH_ERROR";
  Error["InvitationRevokeError"] = "INVITATION_REVOKE_ERROR";
  return Error;
}({});
export const INITIAL_STATE = {
  businessName: '',
  cardInvitationToken: '',
  cardInvitationState: CardInvitation.State.STATE_NONE,
  confirmCancelInvitationModalOpen: false,
  confirmCancelInvitationModalLoading: false,
  error: [],
  invitee: '',
  isLoading: true,
  resendCardInvitationButtonLoading: false,
  resendCardInvitationToastStatus: ResendToastStatus.None
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["Initialize"] = "CARD_INVITATION_MANAGEMENT_INITIALIZE";
  ActionType["RetrieveCardInvitationReceived"] = "CARD_INVITATION_MANAGEMENT_RETRIEVE_CARD_INVITATION_RECEIVED";
  ActionType["RetrieveCardInvitationRejected"] = "CARD_INVITATION_MANAGEMENT_RETRIEVE_CARD_INVITATION_REJECTED";
  ActionType["RevokeCardInvitationReceived"] = "CARD_INVITATION_MANAGEMENT_REVOKE_CARD_INVITATION_RECEIVED";
  ActionType["RevokeCardInvitationRejected"] = "CARD_INVITATION_MANAGEMENT_REVOKE_CARD_INVITATION_REJECTED";
  ActionType["SendCardInvitationReceived"] = "CARD_INVITATION_MANAGEMENT_SEND_CARD_INVITATION_RECEIVED";
  ActionType["SendCardInvitationRejected"] = "CARD_INVITATION_MANAGEMENT_SEND_CARD_INVITATION_REJECTED";
  ActionType["ToggleConfirmCancelInvitationModal"] = "CARD_INVITATION_MANAGEMENT_TOGGLE_CONFIRM_CANCEL_INVITATION_MODAL";
  ActionType["UserClickCancelInvitation"] = "CARD_INVITATION_MANAGEMENT_USER_CLICK_CANCEL_INVITATION";
  ActionType["UserClickConfirmCancelInvitation"] = "CARD_INVITATION_MANAGEMENT_USER_CLICK_CONFIRM_CANCEL_INVITATION";
  ActionType["UserClickResendInvitation"] = "CARD_INVITATION_MANAGEMENT_USER_CLICK_RESEND_INVITATION";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const initializeAction = cardInvitationToken => ({
  type: ActionType.Initialize,
  cardInvitationToken
});
export const retrieveCardInvitationReceivedAction = response => ({
  type: ActionType.RetrieveCardInvitationReceived,
  response
});
export const retrieveCardInvitationRejectedAction = error => ({
  type: ActionType.RetrieveCardInvitationRejected,
  error
});
export const sendCardInvitationReceivedAction = () => ({
  type: ActionType.SendCardInvitationReceived
});
export const sendCardInvitationRejectedAction = error => ({
  type: ActionType.SendCardInvitationRejected,
  error
});
export const revokeCardInvitationReceivedAction = () => ({
  type: ActionType.RevokeCardInvitationReceived
});
export const revokeCardInvitationRejectedAction = error => ({
  type: ActionType.RevokeCardInvitationRejected,
  error
});
export const toggleConfirmCancelInvitationModal = value => ({
  type: ActionType.ToggleConfirmCancelInvitationModal,
  value
});
export const userClickCancelInvitationAction = () => ({
  type: ActionType.UserClickCancelInvitation
});
export const userClickConfirmCancelInvitationAction = () => ({
  type: ActionType.UserClickConfirmCancelInvitation
});
export const userClickResendInvitationAction = () => ({
  type: ActionType.UserClickResendInvitation
});

// Update
// =============================================================================

function handleInitialize(context, state, action) {
  const {
    cardInvitationToken
  } = action;
  const work = () => {
    return context.cardInvitationService.retrieveCardInvitation(RetrieveCardInvitationRequest.create({
      cardInvitationId: cardInvitationToken
    })).then(retrieveCardInvitationReceivedAction, retrieveCardInvitationRejectedAction);
  };
  return [{
    ...state,
    cardInvitationToken
  }, work];
}
function handleRetrieveCardInvitationReceived(context, state, action) {
  try {
    const {
      response
    } = action;
    assert(response.cardInvitation, 'Missing card invitation from invitation response');
    const {
      inviter,
      invitee,
      state: cardInvitationState
    } = response.cardInvitation;
    assert(inviter?.businessName, 'Missing business name from inviter');
    assert(state, 'Missing state from card invitation');
    const inviteeFirstName = invitee?.name?.first ?? '';
    const inviteeLastName = invitee?.name?.last ?? '';
    const inviteeName = `${inviteeFirstName} ${inviteeLastName}`.trim();
    return [{
      ...state,
      businessName: inviter.businessName,
      cardInvitationState,
      invitee: inviteeName,
      isLoading: false
    }, null];
  } catch (error) {
    context.sentry.captureException(`Failed to fetch invitation: ${error}`);
    const {
      error: stateError
    } = state;
    return [{
      ...state,
      error: [...stateError, Error.InvitationFetchError],
      isLoading: false
    }, null];
  }
}
function handleRetrieveCardInvitationRejected(context, state, action) {
  const {
    error
  } = action;
  context.sentry.captureException(`Failed to fetch invitation: ${error}`);
  const {
    error: stateError
  } = state;
  return [{
    ...state,
    error: [...stateError, Error.InvitationFetchError],
    isLoading: false
  }, null];
}
function handleSendCardInvitationReceived(state) {
  return [{
    ...state,
    resendCardInvitationButtonLoading: false,
    resendCardInvitationToastStatus: ResendToastStatus.Success
  }, null];
}
function handleSendCardInvitationRejected(context, state, action) {
  const {
    error
  } = action;
  context.sentry.captureException(`Failed to send invitation: ${error}`);
  return [{
    ...state,
    resendCardInvitationButtonLoading: false,
    resendCardInvitationToastStatus: ResendToastStatus.Failure
  }, null];
}
function handleRevokeCardInvitationReceived(state) {
  return [{
    ...state,
    confirmCancelInvitationModalOpen: false,
    confirmCancelInvitationModalLoading: false
  },
  // refetch card invitation after cancelling
  () => initializeAction(state.cardInvitationToken)];
}
function handleRevokeCardInvitationRejected(context, state, action) {
  const {
    error
  } = action;
  context.sentry.captureException(`Failed to revoke invitation: ${error}`);
  const {
    error: stateError
  } = state;
  return [{
    ...state,
    confirmCancelInvitationModalOpen: false,
    confirmCancelInvitationModalLoading: false,
    error: [...stateError, Error.InvitationRevokeError]
  }, null];
}
function handleToggleConfirmCancelInvitationModal(state, action) {
  const {
    value
  } = action;
  return [{
    ...state,
    confirmCancelInvitationModalOpen: value
  }, null];
}
function handleUserClickCancelInvitation(state) {
  if (state.cardInvitationState !== CardInvitation.State.STATE_OPEN) {
    // TODO(cvu): handle errors
    return [state, null];
  }
  return [{
    ...state,
    confirmCancelInvitationModalOpen: true
  }, null];
}
function handleUserClickConfirmCancelInvitation(context, state) {
  const work = () => context.cardInvitationService.revokeCardInvitation(RevokeCardInvitationRequest.create({
    cardInvitationId: state.cardInvitationToken
  })).then(revokeCardInvitationReceivedAction, revokeCardInvitationRejectedAction);
  return [{
    ...state,
    confirmCancelInvitationModalLoading: true
  }, work];
}
function handleUserClickResendInvitation(context, state) {
  const work = () => context.cardInvitationService.sendCardInvitation(SendCardInvitationRequest.create({
    cardInvitationId: state.cardInvitationToken,
    idempotenceToken: context.uuid()
  })).then(sendCardInvitationReceivedAction, sendCardInvitationRejectedAction);
  return [{
    ...state,
    resendCardInvitationButtonLoading: true,
    // reset toast on button click
    resendCardInvitationToastStatus: ResendToastStatus.None
  }, work];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.Initialize:
      return handleInitialize(context, state, action);
    case ActionType.RetrieveCardInvitationReceived:
      return handleRetrieveCardInvitationReceived(context, state, action);
    case ActionType.RetrieveCardInvitationRejected:
      return handleRetrieveCardInvitationRejected(context, state, action);
    case ActionType.RevokeCardInvitationReceived:
      return handleRevokeCardInvitationReceived(state);
    case ActionType.RevokeCardInvitationRejected:
      return handleRevokeCardInvitationRejected(context, state, action);
    case ActionType.SendCardInvitationReceived:
      return handleSendCardInvitationReceived(state);
    case ActionType.SendCardInvitationRejected:
      return handleSendCardInvitationRejected(context, state, action);
    case ActionType.ToggleConfirmCancelInvitationModal:
      return handleToggleConfirmCancelInvitationModal(state, action);
    case ActionType.UserClickCancelInvitation:
      return handleUserClickCancelInvitation(state);
    case ActionType.UserClickConfirmCancelInvitation:
      return handleUserClickConfirmCancelInvitation(context, state);
    case ActionType.UserClickResendInvitation:
      return handleUserClickResendInvitation(context, state);
  }
};
const cardInvitationManagement = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default cardInvitationManagement;